import { ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { useBulkDelete, useCustomToast } from '@/shared/lib';
import RenameTitleModal from '../ui/RenameTitleModal.vue';
import { MODEL_TITLE_CONFLICT_MESSAGE, isModelTitleConflictError } from '@/features/FormBuilder';
import { deleteMatrixModels, renameMatrixModel } from '../api/matrix-models.api';
import type { ModelItem } from './models.types';

const RENAME_ERROR_MESSAGE = 'Não foi possível renomear o modelo. Tente novamente.';
const MODEL_TITLE_MAX_LENGTH = 100;

export interface ModelActionsOptions {
  /** Refetches the listing, which is what puts the counter and the pagination back in step. */
  refresh: () => Promise<void>;
  /** Writes the new title on the row in place, so a rename does not blink the whole table. */
  patchTitle: (id: number, title: string) => void;
  selectedIds: () => number[];
  removeFromSelection: (ids: number[]) => void;
}

export function useModelActions(options: ModelActionsOptions) {
  const toast = useCustomToast();

  const renamingId = ref<number | null>(null);
  const renameInitialTitle = ref('');
  const isRenaming = ref(false);
  const renameConflictMessage = ref('');

  const renameModal = useModal({
    component: RenameTitleModal,
    attrs: {
      placeholder: 'Digite o título do modelo',
      maxLength: MODEL_TITLE_MAX_LENGTH,
      get initialTitle() {
        return renameInitialTitle.value;
      },
      get isSaving() {
        return isRenaming.value;
      },
      get conflictMessage() {
        return renameConflictMessage.value;
      },
      onClose: () => closeRename(),
      onSave: (title: string) => handleRename(title),
    },
  });

  const closeRename = () => {
    renameModal.close();
    renamingId.value = null;
    renameConflictMessage.value = '';
  };

  const openRename = (model: ModelItem) => {
    renamingId.value = model.id;
    renameInitialTitle.value = model.title;
    renameConflictMessage.value = '';
    return renameModal.open();
  };

  const handleRename = async (title: string) => {
    const id = renamingId.value;
    if (id === null || isRenaming.value) return;

    isRenaming.value = true;
    renameConflictMessage.value = '';

    try {
      await renameMatrixModel(id, title);
      options.patchTitle(id, title);
      closeRename();
      toast.showSuccessToast({ title: 'Modelo renomeado com sucesso.', type: 'success' });
    } catch (error: unknown) {
      if (isModelTitleConflictError(error)) {
        renameConflictMessage.value = MODEL_TITLE_CONFLICT_MESSAGE;
        return;
      }

      toast.showErrorToast({ title: RENAME_ERROR_MESSAGE, type: 'error' });
      console.error('Erro ao renomear o modelo:', error);
    } finally {
      isRenaming.value = false;
    }
  };

  const pendingIds = ref<number[]>([]);

  const { openDeleteConfirmationModal } = useBulkDelete<{ id: number }>({
    entityName: { singular: 'Modelo', plural: 'Modelos' },
    getConfirmTitle: (count) =>
      count <= 1
        ? 'Tem certeza que deseja deletar o Modelo?'
        : 'Tem certeza que deseja deletar os Modelos?',
    successMessage: 'Modelo deletado com sucesso.',
    bulkDeleteApi: deleteMatrixModels,
    onDeleteSuccess: () => {
      options.refresh().catch((error: unknown) => {
        console.error('Erro ao atualizar a listagem após a exclusão:', error);
      });
    },
    onStaleData: () => {
      options.refresh().catch((error: unknown) => {
        console.error('Erro ao atualizar a listagem após a exclusão:', error);
      });
    },
    removeFromSelection: options.removeFromSelection,
    selectedCount: () => pendingIds.value.length,
    selectedIds: () => pendingIds.value,
  });

  const openDeleteConfirmation = async (ids: number[]) => {
    if (ids.length === 0) return;
    pendingIds.value = ids;
    await openDeleteConfirmationModal();
  };

  const openSingleDeleteConfirmation = (model: ModelItem) => openDeleteConfirmation([model.id]);

  const openBulkDeleteConfirmation = () => openDeleteConfirmation(options.selectedIds());

  return {
    openRename,
    openSingleDeleteConfirmation,
    openBulkDeleteConfirmation,
  };
}
